/**
 * Timesheet persistence contract. The Timesheet feature owns the state and
 * writes it to `localStorage`; this module names the key and the stored shape
 * so both stay in one place.
 */
export const TIMESHEET_STORAGE_KEY = "iog-operating-system.timesheet";

/** Deletions live apart from rows, so a stale rows writer cannot erase them. */
export const TIMESHEET_DELETED_KEY = "iog-operating-system.timesheet.deleted";

/** Where the raw value of a damaged store is kept. Written once, never replaced. */
export const TIMESHEET_DAMAGED_KEY = "iog-operating-system.timesheet.damaged";

/** One timed row as it is stored. */
export interface StoredTimesheetRow {
  /** Stable id, also used as the React key. */
  id: string;
  /** What the user typed. Empty until they name the row. */
  name: string;
  /** Accelo task id, or an empty string while no task is picked. */
  taskId: string;
  /**
   * The local day the row belongs to, as `YYYY-MM-DD`. It is the browser's own
   * day, never UTC. A row stored before this field existed gains one on read.
   */
  date: string;
  /** Time banked by earlier runs, in milliseconds. */
  accumulatedMs: number;
  /** Epoch millisecond the current run began, or null when paused. */
  startedAt: number | null;
  /**
   * Epoch millisecond the row's work began. It is set when the row is created
   * and moves only when a person edits the start time. It is not `startedAt`,
   * which restarts on every resume. A row stored before this field existed
   * gains one on read.
   */
  beganAt: number;
  /**
   * The start a person picked from the START list, as an epoch millisecond.
   * Absent when nobody has picked one, which is when the row falls back to
   * `beganAt`. It is the only field a pick writes, so clearing it returns the
   * row to its own start with nothing to recover.
   */
  startOverrideMs?: number;
  /**
   * The IANA timezone the pick was made in. The pick is checked against the
   * row's day in this zone, so opening the page in another timezone keeps it.
   * A pick stored before this field existed is checked in the browser's zone.
   */
  startOverrideZone?: string;
  /** Whether the timer is counting now. */
  running: boolean;
  /** The milliseconds of this row that Accelo already holds. `0` on a new row. */
  publishedMs: number;
  /** Every Accelo activity this row has produced, oldest first. */
  activityIds: string[];
}

/** One row deletion, kept long enough for every open tab to learn it. */
export interface StoredTimesheetDeletion {
  id: string;
  at: number;
}

/** The value stored under `TIMESHEET_STORAGE_KEY`, as JSON. */
export interface StoredTimesheet {
  rows: StoredTimesheetRow[];
  /** Read-only legacy field. New writes keep deletions under `TIMESHEET_DELETED_KEY`. */
  deleted?: StoredTimesheetDeletion[];
}

/**
 * What a merge reads from a row. The landed fields may be absent on a row held
 * in memory from before they existed. Absent reads as nothing landed.
 */
export type MergeableRow = Pick<StoredTimesheetRow, "id" | "accumulatedMs"> &
  Partial<Pick<StoredTimesheetRow, "publishedMs" | "activityIds">>;

/** The row of two with more time, then with more time landed. `mine` wins a tie. */
function largerRow<Row extends MergeableRow>(mine: Row, theirs: Row): Row {
  if (mine.accumulatedMs !== theirs.accumulatedMs) {
    return mine.accumulatedMs > theirs.accumulatedMs ? mine : theirs;
  }
  return (theirs.publishedMs ?? 0) > (mine.publishedMs ?? 0) ? theirs : mine;
}

/** The millisecond a row id encodes, or 0 when it encodes none. */
function rowIdMs(id: string): number {
  const ms = parseInt(id, 36);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Merge two stores, so a second tab loses no row. Union by row id. A row in
 * both keeps the copy with the larger `accumulatedMs`, then the larger
 * `publishedMs`, and the union of both `activityIds`. The result reads newest
 * first by row id.
 */
export function mergeRows<Row extends MergeableRow>(
  mine: readonly Row[],
  theirs: readonly Row[],
  deleted: readonly StoredTimesheetDeletion[] = []
): Row[] {
  const removed = new Set(deleted.map(({ id }) => id));
  const merged = new Map<string, Row>();
  for (const row of [...mine, ...theirs]) {
    if (removed.has(row.id)) continue;
    const held = merged.get(row.id);
    if (held === undefined) {
      merged.set(row.id, row);
      continue;
    }
    const activityIds = [...new Set([...(held.activityIds ?? []), ...(row.activityIds ?? [])])];
    merged.set(row.id, { ...largerRow(held, row), activityIds });
  }
  return [...merged.values()].sort(
    (a, b) => rowIdMs(b.id) - rowIdMs(a.id) || b.id.localeCompare(a.id)
  );
}
